import {useCallback, useEffect, useRef, useState} from 'react';
import {socketService, DisconnectEvent} from '../socket.service';
import {Subscription} from 'rxjs';
import {Misc} from '@/constants';
import ENV from "@/constants/ENV.tsx";

type EventDataMap = Record<string, any>;

interface UseSocketReturn {
    connected: boolean;
    error: any;
    sendMessage: (action: string, data?: any) => boolean;
    send: (data: any) => boolean;
    projectId: string | null;
    sessionId: string | null;
    token: string | null;
    events: EventDataMap;
    connect: (socketUrl: string, authToken?: string) => void;
    disconnect: () => void;
    reconnect: () => void; // Add manual reconnect method
    lastDisconnectEvent: DisconnectEvent | null; // Track disconnect events
    clearEvent: (eventType: string) => void;
}

interface UseSocketOptions {
    eventTypes?: string[];
    autoConnect?: boolean;
    url?: string;
    token?: string;
    persist?: boolean;
    logTag?: string;
    debug?: boolean;
    projectId: string;
    maxReconnectAttempts?: number; // Reconnection options
    reconnectDelay?: number;
    onDisconnect?: (event: DisconnectEvent) => void; // Disconnect callback
    clearEvent?: (eventType: string) => void;
}

// Static reference for singleton socket management
const socketInitialized = {
    value: false,
    url: '',
    token: ''
};

const SOCKET_URL = ENV.SOCKET_URL || '';
const jwtToken = typeof window !== "undefined" ? localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN) ?? "" : undefined;

/**
 * React hook for using the WebSocket service with improved event filtering and disconnect handling
 * @param options - Configuration options for the socket
 * @returns Socket methods and state
 */
export function useSocket(options: UseSocketOptions): UseSocketReturn {
    const {
        eventTypes = ['message'],
        autoConnect = true,
        persist = true,
        logTag = 'useSocket',
        debug = false,
        projectId,
        maxReconnectAttempts = 5,
        reconnectDelay = 3000,
        onDisconnect,
        
    } = options;

    const [connected, setConnected] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [eventData, setEventData] = useState<EventDataMap>({});
    const [lastDisconnectEvent, setLastDisconnectEvent] = useState<DisconnectEvent | null>(null);

    const eventTypesString = eventTypes.join(',');
    const initialized = useRef(false);

    // Debug logger
    const log = useCallback((message: string, ...args: any[]) => {
        if (debug) {
            console.log(`[${logTag}] ${message}`, ...args);
        }
    }, [debug, logTag]);

    // Initialize socket if autoConnect is true or if persisted socket should reconnect
    useEffect(() => {
        if (autoConnect && (!persist || !socketInitialized.value)) {
            log('Initializing socket connection...');

            // Initialize with simplified options (no heartbeat)
            socketService.init({
                baseUrl: SOCKET_URL,
                token: jwtToken,
                debug: debug,
                maxReconnectAttempts,
                reconnectDelay
            });

            if (persist) {
                socketInitialized.value = true;
                socketInitialized.url = SOCKET_URL;
                socketInitialized.token = jwtToken || '';
            }

            initialized.current = true;
        }
        // If we're using a persisted socket and it's already initialized
        else if (persist && socketInitialized.value && !initialized.current) {
            log('Using existing persisted socket connection');
            initialized.current = true;
        }

        // Cleanup function - only disconnect if not persisting
        return () => {
            if (!persist && initialized.current) {
                log('Disconnecting non-persistent socket');
                socketService.disconnect();
                initialized.current = false;
            }
        };
    }, [autoConnect, persist, debug, log, maxReconnectAttempts, reconnectDelay]);

    // Set up subscriptions to socket state and events with filtering
    useEffect(() => {
        const subscriptions: Subscription[] = [];

        // Subscribe to connection status
        subscriptions.push(
            socketService.isConnected().subscribe(isConnected => {
                log('Connection status changed:', isConnected);
                setConnected(isConnected);
            })
        );

        // Subscribe to errors
        subscriptions.push(
            socketService.getErrors().subscribe(err => {
                log('Socket error:', err);
                setError(err);
            })
        );

        // Subscribe to disconnect events
        subscriptions.push(
            socketService.onDisconnect().subscribe(disconnectEvent => {
                log('Socket disconnected:', disconnectEvent);
                setLastDisconnectEvent(disconnectEvent);

                // Call custom disconnect handler if provided
                if (onDisconnect) {
                    onDisconnect(disconnectEvent);
                }
            })
        );

        // Subscribe to specific event types with project ID filtering
        const eventSubscriptions: Subscription[] = eventTypes.map(eventType =>
            socketService.on(eventType).subscribe((eventData: any) => {
                if (String(eventData?.project_id) === String(projectId)) {
                    log(`Received ${eventType} event for project ${projectId}:`, eventData);
                    setEventData(prev => ({ ...prev, [eventType]: eventData }));
                }
            })
        );

        subscriptions.push(...eventSubscriptions);

        return () => {
            subscriptions.forEach(sub => sub.unsubscribe());
        };
    }, [eventTypesString, log, projectId, eventTypes, onDisconnect]);

    // Send message with action (structured message)
    const sendMessage = useCallback((action: string, data: any = {}): boolean => {
        const success = socketService.sendMessage(action, data);
        if (!success) {
            log('Failed to send message - socket not connected');
        }
        return success;
    }, [log]);

    // Send raw data (can be string or object)
    const send = useCallback((data: any): boolean => {
        const success = socketService.send(data);
        if (!success) {
            log('Failed to send data - socket not connected');
        }
        return success;
    }, [log]);

    // Manual connection method - respects persistence setting
    const connect = useCallback((socketUrl: string, authToken?: string): void => {
        log('Manually connecting to WebSocket:', socketUrl);
        socketService.init({
            baseUrl: socketUrl,
            token: authToken,
            debug: debug,
            maxReconnectAttempts,
            reconnectDelay
        });

        if (persist) {
            socketInitialized.value = true;
            socketInitialized.url = socketUrl;
            socketInitialized.token = authToken || '';
        }

        initialized.current = true;
    }, [persist, debug, log, maxReconnectAttempts, reconnectDelay]);

    // Manual disconnect method - respects persistence setting
    const disconnect = useCallback((): void => {
        log('Manually disconnecting socket');
        socketService.disconnect();
        if (persist) {
            socketInitialized.value = false;
            socketInitialized.url = '';
            socketInitialized.token = '';
        }

        initialized.current = false;
    }, [persist, log]);

    // Manual reconnect method
    const reconnect = useCallback((): void => {
        log('Manually triggering reconnection');
        socketService.reconnect();
    }, [log]);

    const clearEvent = useCallback((eventType: string) => {
        setEventData(prev => {
            const newData = { ...prev };
            delete newData[eventType];
            return newData;
        });
    }, []);
    

    return {
        connected,
        error,
        sendMessage,
        send,
        connect,
        disconnect,
        reconnect,
        projectId: socketService.getProjectId(),
        sessionId: socketService.getSessionId(),
        token: socketService.getToken(),
        events: eventData,
        lastDisconnectEvent,
        clearEvent
    };
}
